import { createPathComponent } from '@react-leaflet/core';
import L, { PathOptions } from 'leaflet';
import 'leaflet.markercluster';
import { ReactNode } from 'react';
import { renderToString } from 'react-dom/server';

interface ClusterProps {
  spiderLegPolylineOptions?: PathOptions;
  removeOutsideVisibleBounds?: boolean;
  chunkedLoading?: boolean;
  iconCreateFunction?: (cluster: any) => any;
}

interface Props extends ClusterProps {
  children?: ReactNode;
  icon?: ReactNode;
  disableClusteringAtZoom?: number | string;
  spiderfyOnMaxZoom?: boolean;
}

function buildClusterProps(icon: ReactNode, disableClusteringAtZoom: number | string | undefined, spiderfyOnMaxZoom: boolean, extraProps: Record<string, any>) {
  const clusterProps: Record<string, any> = {
    spiderLegPolylineOptions: { pmIgnore: true, weight: 1.5, color: '#0d6efd', opacity: 0.5 },
    removeOutsideVisibleBounds: true,
    chunkedLoading: true,
    spiderfyOnMaxZoom,
    iconCreateFunction: function (cluster) {
      const childCount = cluster.getChildCount();
      let c = ' marker-cluster-';
      if (childCount < 10) {
        c += 'small';
      } else if (childCount < 100) {
        c += 'medium';
      } else {
        c += 'large';
      }
      return new L.DivIcon({
        html: icon
          ? renderToString(
              <div
                style={{ fontSize: '10px' }}
                className={'d-flex justify-content-center align-items-center'}
              >
                <span>{childCount}</span>
                {icon}
              </div>
            )
          : renderToString(
              <div style={{ fontSize: '10px' }}>
                <span>{childCount}</span>
              </div>
            ),
        className: 'marker-cluster' + c,
        iconSize: new L.Point(40, 40)
      });
    }
  };

  if (disableClusteringAtZoom) {
    clusterProps['disableClusteringAtZoom'] = disableClusteringAtZoom;
  }

  // Separate event handlers from regular props
  const clusterEvents: Record<string, any> = {};
  Object.entries(extraProps).forEach(([propName, prop]) => {
    if (propName.startsWith('on')) {
      clusterEvents[propName] = prop;
    } else {
      clusterProps[propName] = prop;
    }
  });

  return { clusterProps, clusterEvents };
}

const MarkerClusterGroup = createPathComponent(
  (
    {
      children: _c,
      icon = null,
      disableClusteringAtZoom,
      spiderfyOnMaxZoom = false,
      ...props
    }: Props,
    ctx
  ) => {
    const { clusterProps, clusterEvents } = buildClusterProps(icon, disableClusteringAtZoom, spiderfyOnMaxZoom, props);

    // @ts-ignore markerClusterGroup plugin types not available
    const markerClusterGroup = new L.markerClusterGroup(clusterProps);

    // Register event listeners
    Object.entries(clusterEvents).forEach(([eventAsProp, callback]) => {
      const clusterEvent = `cluster${eventAsProp.substring(2).toLowerCase()}`;
      markerClusterGroup.on(clusterEvent, callback);
    });

    return {
      instance: markerClusterGroup,
      context: { ...ctx, layerContainer: markerClusterGroup }
    };
  },
  // updateProps: called by React Leaflet when props change, without remounting
  (instance, props, prevProps) => {
    const { disableClusteringAtZoom, spiderfyOnMaxZoom, icon, ...rest } = props as Props & Record<string, any>;
    const { disableClusteringAtZoom: prevDisable, spiderfyOnMaxZoom: prevSpiderfy } = prevProps as Props;

    if (disableClusteringAtZoom !== prevDisable || spiderfyOnMaxZoom !== prevSpiderfy) {
      // These options can't be updated on a live instance — set them on the internal options object
      if (disableClusteringAtZoom !== prevDisable) {
        instance.options.disableClusteringAtZoom = disableClusteringAtZoom;
      }
      if (spiderfyOnMaxZoom !== prevSpiderfy) {
        instance.options.spiderfyOnMaxZoom = spiderfyOnMaxZoom;
      }
      // Refresh clusters to pick up new options
      instance.refreshClusters();
    }
  }
);

export default MarkerClusterGroup;
